import type { ReactNode } from "react";
import css from "./Switch.module.css";

/**
 * Neon slide-switch per screen-03 эталон.
 *
 * Two visual sizes:
 *   - default (40×22, knob 16×16) — ЧАЙНАЯ.
 *   - variant="bilead" (40×20, knob 14×14) — type-pill (лид ↔ бронь).
 *
 * Track is a rounded pill, knob is a smooth circle. NO notch/grip.
 *
 * Label typography depends on checked state:
 *   - OFF: lowercase Rajdhani 13/500, muted text-2.
 *   - ON:  uppercase Orbitron 10/700, accent color, glow.
 *
 * The accent color drives both border and ON-state fill; pass it via the
 * `accent` prop ("var(--green)", "var(--purple)", etc).
 */
interface SwitchProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** Inline label rendered to the right of the switch. */
  label?: ReactNode;
  /** CSS-color expression for the accent (default: var(--green)). */
  accent?: string;
  /** Visual variant — default = standard 40×22 neon; "bilead" = type-pill. */
  variant?: "neon" | "bilead";
  disabled?: boolean;
  ariaLabel?: string;
}

export function Switch({
  checked,
  onChange,
  label,
  accent,
  variant = "neon",
  disabled,
  ariaLabel,
}: SwitchProps) {
  const style = accent
    ? ({ ["--switch-accent" as string]: accent } as React.CSSProperties)
    : undefined;
  return (
    <label
      className={`${css.wrap} ${variant === "bilead" ? css.bilead : ""} ${disabled ? css.disabled : ""}`}
      style={style}
    >
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={ariaLabel}
        disabled={disabled}
        className={`${css.track} ${checked ? css.on : ""}`}
        onClick={() => !disabled && onChange(!checked)}
      >
        <span className={css.knob} aria-hidden="true" />
      </button>
      {label != null ? (
        <span className={`${css.label} ${checked ? css.labelOn : ""}`}>{label}</span>
      ) : null}
    </label>
  );
}
